<div class="container mx-auto px-4" [class.dark]="themeService.isDark()">
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">

    <!-- Calculator Form -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
      <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-6">Calculate CAGR</h2>

      <!-- Form Fields -->
      <form [formGroup]="calculatorForm" (ngSubmit)="calculateCAGR()">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <!-- Left Column -->
          <div class="space-y-4">

            <!-- Most Recent Value -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Most Recent Value (Ending Value) *
              </label>
              <input
                type="number"
                formControlName="endingValue"
                step="0.01"
                min="0"
                placeholder="Enter the most recent value"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                [class.border-red-500]="calculatorForm.get('endingValue')?.invalid && calculatorForm.get('endingValue')?.touched"
              />
              <div *ngIf="calculatorForm.get('endingValue')?.invalid && calculatorForm.get('endingValue')?.touched"
                   class="text-red-500 text-sm mt-1">
                Most recent value is required and must be positive
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                The current or most recent value of the metric you're analyzing
              </p>
            </div>

                 <!-- Number of Periods -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Number of Periods *
              </label>
              <input
                type="number"
                formControlName="periods"
                step="0.01"
                min="0.01"
                placeholder="Enter number of periods"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                [class.border-red-500]="calculatorForm.get('periods')?.invalid && calculatorForm.get('periods')?.touched"
              />
              <div *ngIf="calculatorForm.get('periods')?.invalid && calculatorForm.get('periods')?.touched"
                   class="text-red-500 text-sm mt-1">
                Number of periods is required and must be greater than 0
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Number of years, quarters, or other time periods between the values
              </p>
            </div>
          
          </div>

          <!-- Right Column -->
          <div class="space-y-4">

            <!-- Earliest Value -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Earliest Value (Beginning Value) *
              </label>
              <input
                type="number"
                formControlName="beginningValue"
                step="0.0000000001"
                min="0"
                placeholder="Enter the earliest value"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                [class.border-red-500]="calculatorForm.get('beginningValue')?.invalid && calculatorForm.get('beginningValue')?.touched"
              />
              <div *ngIf="calculatorForm.get('beginningValue')?.invalid && calculatorForm.get('beginningValue')?.touched"
                   class="text-red-500 text-sm mt-1">
                Earliest value is required and must be positive
              </div>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                The starting or earliest value of the metric you're analyzing
              </p>
            </div>
          </div>

        </div>

        <!-- Year Helper Section - Full Width Row -->
        <div class="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            <svg class="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"></path>
            </svg>
            Year Helper (Optional)
          </h3>
          <div class="grid grid-cols-2 gap-4">
            <!-- Start Year -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Year
              </label>
              <input
                type="number"
                formControlName="startYear"
                step="1"
                min="1900"
                max="2100"
                placeholder="e.g., 2020"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                (input)="updatePeriodsFromYears()"
              />
            </div>
            <!-- End Year -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Year
              </label>
              <input
                type="number"
                formControlName="endYear"
                step="1"
                min="1900"
                max="2100"
                placeholder="e.g., 2024"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                (input)="updatePeriodsFromYears()"
              />
            </div>
          </div>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Enter start and end years to automatically calculate the number of periods
          </p>
        </div>

        <!-- Calculate Button -->
        <div class="pt-6">
          <button
            type="submit"
            class="w-full text-white bg-green-700 hover:bg-green-800 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-lg px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            [disabled]="calculatorForm.invalid || isCalculating">
            {{ isCalculating ? 'Calculating...' : 'Calculate CAGR' }}
          </button>
        </div>

        <!-- Clear Button -->
        <div class="text-center pt-4">
          <button
            type="button"
            class="px-6 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 font-medium rounded-lg flex items-center mx-auto"
            (click)="clearForm()">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            Clear All
          </button>
        </div>

      </form>
    </div>

    <!-- Results Panel -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
      <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-6">CAGR Results</h2>

      <!-- Results Display -->
      <div *ngIf="cagrResult !== null; else noResults">

        <!-- Main CAGR Result -->
        <div class="mb-6 p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div class="text-center">
            <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">Compound Annual Growth Rate</h3>
            <div class="text-4xl font-bold mb-2"
                 [ngClass]="cagrResult >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
              {{ cagrResult | number:'1.2-2' }}%
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ cagrResult >= 0 ? 'Annual Growth' : 'Annual Decline' }}
            </p>
          </div>
        </div>

        <!-- Growth Interpretation -->
        <div class="mb-6 p-4 rounded-lg"
             [ngClass]="getInterpretationClass()">
          <h4 class="font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <svg [class]="getInterpretationIcon()" class="w-5 h-5 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            Growth Analysis
          </h4>
          <p class="text-sm">{{ getInterpretation() }}</p>
        </div>

        <!-- Future Value Projection -->
        <div *ngIf="cagrResult > 0" class="p-4 mb-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <h4 class="font-semibold text-gray-700 dark:text-gray-300 mb-3">Future Value Projection</h4>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
            If this growth rate continues, the value would be:
          </p>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-700 dark:text-gray-300">In 3 years:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ getFutureValue(3) | number:'1.2-2' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-700 dark:text-gray-300">In 5 years:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ getFutureValue(5) | number:'1.2-2' }}</span>
            </div>
             <div class="flex justify-between">
              <span class="text-gray-700 dark:text-gray-300">In 10 years:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ getFutureValue(10) | number:'1.2-2' }}</span>
            </div>
          </div>
        </div>

        <!-- Calculation Breakdown -->
        <div class="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <h4 class="font-semibold text-gray-700 dark:text-gray-300 mb-3">Calculation Details</h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Ending Value:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ calculatorForm.get('endingValue')?.value | number:'1.2-4' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Beginning Value:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ calculatorForm.get('beginningValue')?.value | number:'1.2-4' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Number of Periods:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ calculatorForm.get('periods')?.value | number:'1.0-2' }}</span>
            </div>
            <div class="flex justify-between border-t pt-2">
              <span class="text-gray-600 dark:text-gray-400">Total Growth:</span>
              <span class="font-medium" [ngClass]="totalGrowth >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                {{ totalGrowth | number:'1.2-2' }}%
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- No Results Template -->
      <ng-template #noResults>
        <div class="text-center py-8">
          <svg class="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          <h3 class="text-lg font-semibold text-gray-600 dark:text-gray-400 mb-2">No Results Yet</h3>
          <p class="text-gray-500 dark:text-gray-400">
            Fill in the form and click "Calculate CAGR" to see your growth rate analysis.
          </p>
        </div>
      </ng-template>

      <!-- Error Message -->
      <div *ngIf="errorMessage" class="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div class="flex items-center">
          <svg class="w-5 h-5 text-red-500 dark:text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
          <span class="text-red-700 dark:text-red-300 text-sm">{{ errorMessage }}</span>
        </div>
      </div>
    </div>

  </div>

  <!-- Information Section -->
  <div class="mt-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
    <h2 class="text-2xl font-bold text-gray-800 dark:text-white mb-6">About CAGR (Compound Annual Growth Rate)</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div>
        <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">What is CAGR?</h3>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          CAGR represents the avg annual growth rate of an investment over a specified period of time.
          It's one of the most accurate ways to calculate and compare returns for anything that can rise or fall in value over time.
        </p>
        <p class="text-gray-600 dark:text-gray-400">
          Unlike simple average returns, CAGR accounts for the compounding effect, providing a more realistic picture
          of investment performance over time.
        </p>
      </div>
      <div>
        <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">Common Uses</h3>
        <ul class="text-gray-600 dark:text-gray-400 space-y-2">
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            <span><strong>Revenue Growth:</strong> Track company sales expansion over time</span>
          </li>
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            <span><strong>Earnings Growth:</strong> Analyze profit growth consistency</span>
          </li>
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            <span><strong>Investment Returns:</strong> Compare portfolio performance</span>
          </li>
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            <span><strong>Market Analysis:</strong> Evaluate sector or market growth</span>
          </li>
          <li class="flex items-start">
            <svg class="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
            <span><strong>Business Metrics:</strong> Assess any measurable business growth</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Formula Explanation -->
    <div class="mt-8 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3">CAGR Formula Explained</h3>
      <div class="bg-white dark:bg-gray-800 p-4 rounded border text-center mb-4">
        <code class="text-lg text-gray-900 dark:text-white">CAGR = (Ending Value / Beginning Value)^(1/Number of Periods) - 1</code>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div class="text-gray-600 dark:text-gray-400">
          <strong class="text-gray-700 dark:text-gray-300">Ending Value:</strong> The final value at the end of the period
        </div>
        <div class="text-gray-600 dark:text-gray-400">
          <strong class="text-gray-700 dark:text-gray-300">Beginning Value:</strong> The initial value at the start of the period
        </div>
        <div class="text-gray-600 dark:text-gray-400">
          <strong class="text-gray-700 dark:text-gray-300">Number of Periods:</strong> The number of years (or other time units) between the values
        </div>
      </div>
    </div>
  </div>
</div>

